/**
 * Analytics shared UI — Highcharts wrappers, KPI cards, breakdown tables, the
 * date-range toolbar and loading/empty/error states (FEAT-ANALYTICS-001).
 * Chakra + Highcharts (both already in the app). Self-contained — copied/own
 * components, not imported from Boards.
 */
import React from 'react';
import {
  Box, Flex, Text, SimpleGrid, Select, Input, Spinner, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Badge, HStack, Heading, Icon, Stack,
} from '@chakra-ui/react';
import { FiArrowUpRight, FiArrowDownRight } from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// Resolve the tenant brand colour (canvas can't read CSS vars) with a safe fallback.
export function brandColor() {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim();
    return v || '#2563eb';
  } catch {
    return '#2563eb';
  }
}
// GA-style multi-series palette, led by the xNotify brand blue (our colours, GA layout).
const PALETTE = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#475569'];

// Shared surface tokens for a clean, airy card system.
const CARD = {
  bg: 'white',
  borderWidth: '1px',
  borderColor: '#ebedf0',
  borderRadius: '14px',
  boxShadow: '0 1px 2px rgba(16,24,40,.04)',
};
const LABEL = { fontSize: '11px', fontWeight: '600', letterSpacing: '0.04em', color: 'gray.500', textTransform: 'uppercase' };

export const fmtInt = (n) => Number(n || 0).toLocaleString();
export const fmtPct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
export const fmtDuration = (s) => {
  s = Number(s || 0);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const baseChart = {
  credits: { enabled: false },
  title: { text: undefined },
  legend: { enabled: false },
  chart: { style: { fontFamily: 'inherit' }, backgroundColor: 'transparent' },
};

export function TrendChart({ categories = [], series = [], height = 260 }) {
  const options = {
    ...baseChart,
    chart: { ...baseChart.chart, type: 'areaspline', height },
    xAxis: { categories, tickLength: 0, lineColor: '#e5e7eb', labels: { style: { color: '#9ca3af', fontSize: '10px' } } },
    yAxis: { title: { text: null }, gridLineColor: '#f1f5f9', labels: { style: { color: '#9ca3af', fontSize: '10px' } } },
    legend: { enabled: series.length > 1, itemStyle: { fontSize: '11px', color: '#6b7280' } },
    plotOptions: { areaspline: { fillOpacity: 0.12, marker: { enabled: false }, lineWidth: 2 } },
    colors: PALETTE,
    series: series.map((s) => ({ name: s.name, data: s.data })),
  };
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

/** Horizontal bar from [{label, value}]. */
export function BarChart({ rows = [], height = 280, color }) {
  const cats = rows.map((r) => String(r.label));
  const data = rows.map((r) => Number(r.value));
  const options = {
    ...baseChart,
    chart: { ...baseChart.chart, type: 'bar', height },
    xAxis: { categories: cats, lineColor: '#e5e7eb', labels: { style: { color: '#374151', fontSize: '11px' } } },
    yAxis: { title: { text: null }, gridLineColor: '#f1f5f9', labels: { style: { color: '#9ca3af', fontSize: '10px' } } },
    plotOptions: { bar: { borderRadius: 3, color: color || brandColor(), dataLabels: { enabled: false } } },
    series: [{ data }],
  };
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

/** Clean ranked bar-list from [{label, value}] — label · inline bar · value · %. */
export function BreakdownTable({ rows = [], labelHeader = 'Name', valueHeader = 'Users', limit = 8 }) {
  const total = rows.reduce((s, r) => s + Number(r.value || 0), 0) || 1;
  const max = Math.max(1, ...rows.map((r) => Number(r.value || 0)));
  const shown = rows.slice(0, limit);
  if (!shown.length) return <EmptyState label="No data for this range" minimal />;
  return (
    <Stack spacing={2.5}>
      <Flex justify="space-between" px="2px">
        <Text {...LABEL} fontSize="10px">{labelHeader}</Text>
        <Text {...LABEL} fontSize="10px">{valueHeader}</Text>
      </Flex>
      {shown.map((r, i) => {
        const v = Number(r.value || 0);
        const pct = (v / total) * 100;
        const w = Math.max(2, (v / max) * 100);
        return (
          <Box key={i}>
            <Flex justify="space-between" align="baseline" mb="3px" gap={2}>
              <Text fontSize="13px" color="gray.700" noOfLines={1} title={r.title || String(r.label)}>{String(r.label)}</Text>
              <Text fontSize="13px" color="gray.900" fontWeight="600" whiteSpace="nowrap" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtInt(v)} <Text as="span" color="gray.400" fontWeight="400" fontSize="11px">{pct.toFixed(1)}%</Text>
              </Text>
            </Flex>
            <Box h="5px" bg="gray.100" borderRadius="full" overflow="hidden">
              <Box h="100%" w={`${w}%`} bg="var(--brand-primary)" opacity={0.85} borderRadius="full" />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

/** GA-style breakdown card: title, #1 headline, a mini bar chart, then the bar-list. */
export function BreakdownCard({ title, rows = [], labelHeader = 'Name', valueHeader = 'Users', limit = 7 }) {
  const list = rows || [];
  const top = list[0];
  const total = list.reduce((s, r) => s + Number(r.value || 0), 0) || 1;
  const bars = list.slice(0, 12).map((r) => Number(r.value || 0));
  return (
    <Box {...CARD} p={4}>
      <Text {...LABEL} mb={2}>{title}</Text>
      {top ? (
        <>
          <Text fontSize="12px" color="gray.500" noOfLines={1}>#1 · {String(top.label)}</Text>
          <Flex align="baseline" gap={2} mt="2px" mb={2}>
            <Text fontSize="24px" fontWeight="700" color="gray.900" lineHeight="1" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(top.value)}</Text>
            <Text fontSize="12px" color="gray.400">{((Number(top.value) / total) * 100).toFixed(1)}%</Text>
          </Flex>
          <Box mb={3} opacity={0.9}><Sparkline data={bars} type="column" height={32} /></Box>
          <BreakdownTable rows={list} labelHeader={labelHeader} valueHeader={valueHeader} limit={limit} />
        </>
      ) : (
        <EmptyState label="No data for this range" minimal />
      )}
    </Box>
  );
}

/** Tiny inline trend chart for cards (no axes/labels). */
export function Sparkline({ data = [], color, height = 36, type = 'areaspline' }) {
  if (!data || data.length < 2) return <Box h={`${height}px`} />;
  const options = {
    credits: { enabled: false },
    title: { text: undefined },
    chart: { type, height, backgroundColor: 'transparent', margin: [2, 0, 0, 0], spacing: [0, 0, 0, 0], style: { fontFamily: 'inherit' } },
    xAxis: { visible: false },
    yAxis: { visible: false, endOnTick: false, startOnTick: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      series: { animation: false, marker: { enabled: false }, states: { hover: { enabled: false } }, enableMouseTracking: false },
      areaspline: { fillOpacity: 0.1, lineWidth: 1.75 },
      column: { borderWidth: 0, pointPadding: 0.05, groupPadding: 0.05 },
    },
    colors: [color || brandColor()],
    series: [{ data }],
  };
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export function StatCard({ label, value, delta, suffix, help, spark }) {
  const up = delta != null && delta >= 0;
  return (
    <Box {...CARD} p={4}>
      <Text {...LABEL}>{label}</Text>
      <Flex align="baseline" gap={2} mt={1.5}>
        <Text fontSize="28px" fontWeight="700" color="gray.900" lineHeight="1" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {value}{suffix ? <Text as="span" fontSize="15px" color="gray.400" ml="1px">{suffix}</Text> : null}
        </Text>
        {delta != null && (
          <HStack spacing="1px" color={up ? 'green.600' : 'red.500'} fontSize="xs" fontWeight="600">
            <Icon as={up ? FiArrowUpRight : FiArrowDownRight} />
            <Text>{Math.abs(delta).toFixed(1)}%</Text>
          </HStack>
        )}
      </Flex>
      {help && <Text fontSize="11px" color="gray.400" mt="3px">{help}</Text>}
      {spark && spark.length > 1 && <Box mt={2}><Sparkline data={spark} /></Box>}
    </Box>
  );
}

export const StatGrid = ({ children, columns }) => (
  <SimpleGrid columns={columns || { base: 2, md: 4 }} spacing={3}>{children}</SimpleGrid>
);

export function Panel({ title, children, action }) {
  return (
    <Box {...CARD} p={{ base: 4, md: 5 }}>
      {title && (
        <Flex align="center" justify="space-between" mb={4}>
          <Text {...LABEL}>{title}</Text>
          {action}
        </Flex>
      )}
      {children}
    </Box>
  );
}

export function EmptyState({ label = 'No data', minimal }) {
  return (
    <Flex align="center" justify="center" py={minimal ? 6 : 12} color="gray.400" fontSize="sm">
      {label}
    </Flex>
  );
}

export function ReportFrame({ loading, error, children }) {
  if (loading) return <Flex align="center" justify="center" py={20}><Spinner color="var(--brand-primary)" /></Flex>;
  if (error) return (
    <Flex direction="column" align="center" justify="center" py={16} gap={2}>
      <Badge colorScheme="red">Error</Badge>
      <Text color="gray.500" fontSize="sm">{error}</Text>
    </Flex>
  );
  return children;
}

/** Preset date-range selector + optional custom from/to. */
export function DateRangeBar({ value, onChange }) {
  const { mode, range, from, to } = value;
  return (
    <HStack spacing={2} flexWrap="wrap">
      <Select
        size="sm" w="auto" value={mode === 'custom' ? 'custom' : range}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'custom') onChange({ ...value, mode: 'custom' });
          else onChange({ ...value, mode: 'preset', range: v });
        }}
      >
        <option value="last_7_days">Last 7 days</option>
        <option value="last_30_days">Last 30 days</option>
        <option value="last_90_days">Last 90 days</option>
        <option value="last_year">Last 12 months</option>
        <option value="custom">Custom…</option>
      </Select>
      {mode === 'custom' && (
        <>
          <Input size="sm" type="date" w="auto" value={from || ''} onChange={(e) => onChange({ ...value, from: e.target.value })} />
          <Text color="gray.400" fontSize="sm">→</Text>
          <Input size="sm" type="date" w="auto" value={to || ''} onChange={(e) => onChange({ ...value, to: e.target.value })} />
        </>
      )}
    </HStack>
  );
}
